import { redirect } from "@tanstack/react-router";

import { hasRole, type UserRole } from "../auth/role-policy";
import { AUTH_PROVIDER_COMPLETION_PATH } from "../configs/auth-provider.config";
import {
  type AuthenticationReturnLocation,
  getAuthenticationReturnToFromLocation,
} from "./auth-return-intent";

export type AuthUserProjection = {
  id: string;
  role?: string;
  companyRole?: string | null;
};

export type AuthSnapshot =
  | { status: "restoring"; user?: undefined }
  | { status: "anonymous"; user?: undefined }
  | { status: "authenticated"; user: AuthUserProjection };

export type AuthProjection = {
  status: AuthSnapshot["status"];
  user?: AuthUserProjection;
  ready: () => Promise<AuthSnapshot>;
  subscribe: (listener: () => void) => () => void;
};

export type RouterContext = {
  auth: AuthProjection;
};

type ReadySnapshot = Exclude<AuthSnapshot, { status: "restoring" }>;
type ReadinessObserver = {
  resolve: (snapshot: AuthSnapshot) => void;
  reject: (error: unknown) => void;
};
type ReadinessSource = {
  subscribe: (observer: ReadinessObserver) => () => void;
  invalidateRead: () => void;
  isWaiting: () => boolean;
};
// One unresolved external read can outlive all routes. Keep its continuation
// here, with no cancelled navigation observers, and reuse it on the next visit.
const readinessSources = new WeakMap<AuthProjection, ReadinessSource>();

function observeReadiness(auth: AuthProjection, observer: ReadinessObserver) {
  let source = readinessSources.get(auth);
  if (!source) {
    const observers = new Set<ReadinessObserver>();
    let unsubscribe: (() => void) | undefined;
    let revision = 0;
    let pendingRevision: number | undefined;
    let pendingReads = 0;
    const cleanup = () => {
      if (observers.size) return;
      unsubscribe?.();
      unsubscribe = undefined;
      if (pendingReads === 0) readinessSources.delete(auth);
    };
    const checkReadiness = () => {
      if (!observers.size || pendingRevision === revision) return;
      const requestedRevision = revision;
      pendingRevision = revision;
      pendingReads += 1;
      void Promise.resolve()
        .then(() => auth.ready())
        .then(
          (next) => {
            pendingReads -= 1;
            if (pendingRevision === requestedRevision)
              pendingRevision = undefined;
            if (requestedRevision === revision) {
              for (const observer of [...observers]) observer.resolve(next);
            }
            cleanup();
          },
          (error: unknown) => {
            pendingReads -= 1;
            if (pendingRevision === requestedRevision)
              pendingRevision = undefined;
            if (requestedRevision === revision) {
              for (const observer of [...observers]) observer.reject(error);
            }
            cleanup();
          },
        );
    };
    source = {
      isWaiting: () => observers.size > 0,
      // Advance before router invalidation can abort the active guards and
      // remove their auth subscription, so the successor cannot reuse stale reads.
      invalidateRead: () => {
        revision += 1;
      },
      subscribe: (observer) => {
        observers.add(observer);
        if (!unsubscribe) {
          unsubscribe = auth.subscribe(() => {
            revision += 1;
            checkReadiness();
          });
        }
        checkReadiness();
        return () => {
          observers.delete(observer);
          cleanup();
        };
      },
    };
    readinessSources.set(auth, source);
  }
  return source.subscribe(observer);
}

export type RouterAuthSnapshot =
  | { status: "anonymous" }
  | { status: "authenticated"; roles?: readonly string[] };

export type RouterAuthContext = AuthProjection;
export type AuthInput = AuthProjection | AuthSnapshot | RouterAuthSnapshot;

export function createRouterAuthContext(
  initialSnapshot: AuthInput = { status: "anonymous" },
): AuthProjection {
  if ("ready" in initialSnapshot) return initialSnapshot;

  const snapshot: AuthSnapshot =
    initialSnapshot.status === "authenticated"
      ? {
          status: "authenticated",
          user:
            "user" in initialSnapshot
              ? initialSnapshot.user
              : {
                  id: "test-user",
                  role: initialSnapshot.roles?.[0],
                },
        }
      : initialSnapshot;

  return {
    status: snapshot.status,
    ...(snapshot.status === "authenticated" ? { user: snapshot.user } : {}),
    ready: async () => snapshot,
    subscribe: () => () => undefined,
  };
}

function readPublicSnapshot(auth: AuthProjection, signal: AbortSignal) {
  return new Promise<AuthSnapshot>((resolve, reject) => {
    let unsubscribe = () => {};
    const cleanup = () => {
      unsubscribe();
      signal.removeEventListener("abort", cancel);
    };
    const cancel = () => {
      if (!signal.aborted) return;
      cleanup();
      reject(signal.reason);
    };
    signal.addEventListener("abort", cancel, { once: true });
    unsubscribe = observeReadiness(auth, {
      resolve: (snapshot) => {
        cleanup();
        resolve(snapshot);
      },
      reject: (error) => {
        cleanup();
        reject(error);
      },
    });
  });
}

export function subscribeRouterToAuthInvalidation(
  auth: AuthProjection,
  invalidate: () => Promise<unknown>,
) {
  let previousStatus = auth.status;
  return auth.subscribe(() => {
    const wasRestoring = previousStatus === "restoring";
    previousStatus = auth.status;
    const readiness = readinessSources.get(auth);
    // Pending auth guards already consume the initial restoration result.
    // Reloading here cancels that handoff and starts a redundant auth traversal.
    if (wasRestoring && readiness?.isWaiting()) return;
    readiness?.invalidateRead();
    void invalidate();
  });
}

export async function redirectAuthenticatedUser({
  context,
  abortController,
}: {
  context: RouterContext;
  abortController: AbortController;
}) {
  if (abortController.signal.aborted) return;
  const snapshot = await readPublicSnapshot(
    context.auth,
    abortController.signal,
  );
  abortController.signal.throwIfAborted();

  if (snapshot.status === "authenticated") {
    throw redirect({ to: "/home", replace: true });
  }
}

export async function requireAuthenticated(
  auth: AuthProjection,
  location: AuthenticationReturnLocation,
  signal: AbortSignal,
): Promise<Extract<AuthSnapshot, { status: "authenticated" }>> {
  signal.throwIfAborted();
  // Each traversal owns its signal. Release canceled observers immediately;
  // the shared readiness source still deduplicates the external session read.
  const snapshot = await new Promise<ReadySnapshot>((resolve, reject) => {
    let unsubscribeReadiness = () => {};
    const cleanup = () => {
      unsubscribeReadiness();
      signal.removeEventListener("abort", cancel);
    };
    const cancel = () => {
      cleanup();
      reject(signal.reason);
    };
    signal.addEventListener("abort", cancel, { once: true });
    unsubscribeReadiness = observeReadiness(auth, {
      resolve: (next) => {
        if (next.status === "restoring") return;
        cleanup();
        resolve(next);
      },
      reject: (error) => {
        cleanup();
        reject(error);
      },
    });
  });
  signal.throwIfAborted();

  if (snapshot.status !== "authenticated") {
    if (/^\/answer\/[^/]+\/preview\/?$/.test(location.pathname)) {
      throw redirect({ to: "/preview-access-denied", replace: true });
    }
    const returnTo = getAuthenticationReturnToFromLocation(location);
    throw redirect({
      to: "/sign-in",
      search: { redirectUrl: returnTo },
    });
  }

  requireRoles(snapshot, []);
  return snapshot;
}

export async function redirectAuthenticated(
  auth: AuthProjection,
  pathname: string,
  signal: AbortSignal,
) {
  if (signal.aborted) return;
  const snapshot = await readPublicSnapshot(auth, signal);
  signal.throwIfAborted();

  if (
    snapshot.status === "authenticated" &&
    pathname !== AUTH_PROVIDER_COMPLETION_PATH &&
    pathname !== "/forgot-password"
  ) {
    throw redirect({ to: "/home" });
  }
}

export function requireRoles(
  snapshot: Extract<AuthSnapshot, { status: "authenticated" }>,
  requiredRoles: readonly UserRole[],
) {
  if (!hasRole(snapshot.user, requiredRoles)) {
    throw redirect({ to: "/403", replace: true });
  }
}

export function requireCompanyRole(
  snapshot: Extract<AuthSnapshot, { status: "authenticated" }>,
  companyRole: "Owner",
) {
  if (snapshot.user.companyRole !== companyRole) {
    throw redirect({ to: "/403", replace: true });
  }
}

export function createAnonymousAuthProjection(): AuthProjection {
  return {
    status: "anonymous",
    ready: async () => ({ status: "anonymous" }),
    subscribe: () => () => undefined,
  };
}
